import {
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type Ref,
} from 'react';
import { styled } from '@mui/material/styles';

import AccountIcon from './AccountIcon';
import { BRAND } from '../shared/theme/paletteDark';
import { FONTS } from '../shared/theme/typography';
import {
  TOGGLE_FILL,
  TOGGLE_LABELS,
  toggleMetrics,
  type ToggleItem,
  type TogglePill,
} from './viewToggleMetrics';

/**
 * Explore · My Schedule · Account.
 *
 * Three items of one design, drawn from `viewToggleMetrics`: a pill with a
 * parchment highlight inset from its edges, and boxes tiled between those
 * insets. Account is a circle, so the highlight becomes one on the way in and
 * a pill again on the way out. "Sign In", signed out, has no box of its own
 * and is never highlighted: it is an action, not a view.
 *
 * The highlight is three pieces — two round caps and a middle — rather than one
 * box, because `scaleX` on a rounded box flattens its caps. Position and width
 * therefore move as transforms only, which keeps the move running on the
 * compositor while React renders the view behind it. Labels cross-fade between
 * regular parchment and bold black, since `font-weight` cannot animate; the
 * boxes are fixed, so nothing reflows underneath.
 *
 * It paints imperatively through `follow`, so that a tap moves it on the next
 * frame rather than after the view it switches to has rendered, and so a phone's
 * page swipe can drag it along with the pages.
 *
 * Reduced motion is handled globally, in `fonts.css`: every transition here is
 * a CSS one, and that rule cuts its duration.
 */

export type ViewName = 'explore' | 'schedule';
/** What the toggle can be showing: a view, or the account page. */
export type ToggleValue = ViewName | 'account';

/** The move, on every layout: the repo's `EASE_IN_OUT`, which the page swipe uses too. */
export const TOGGLE_MOVE_MS = 420;
export const TOGGLE_EASE = 'cubic-bezier(0.65, 0, 0.35, 1)';

export interface ViewToggleHandle {
  /** Paint at a float item index — 0 Explore, 1 My Schedule, 2 Account — over `ms`. */
  follow: (index: number, ms: number, ease: string) => void;
}

const Pill = styled('div')({
  position: 'relative',
  flexShrink: 0,
  borderRadius: 999,
  userSelect: 'none',
  WebkitTapHighlightColor: 'transparent',
});

/** Carries the highlight's position; the pieces inside carry its width. */
const Highlight = styled('div')({
  position: 'absolute',
  left: 0,
  pointerEvents: 'none',
  willChange: 'transform',
});

const Cap = styled('span')({
  position: 'absolute',
  top: 0,
  left: 0,
  borderRadius: 999,
  background: BRAND.parchment,
});

/**
 * Square-edged, stretched between the cap centres, so the caps keep their shape.
 *
 * It is drawn one highlight wide rather than one pixel, because the browser
 * snaps a box to whole device pixels before it scales it: a 1px bar at 175%
 * zoom on a 2x screen is painted 4 device pixels wide instead of 3.5, and
 * `scaleX` multiplies that 14% by the length, running the bar a dozen pixels
 * past the trailing cap and baking its square corners into the pill's edge.
 * A bar the height of the highlight leaves under half a pixel of that error.
 */
const Mid = styled('span')({
  position: 'absolute',
  top: 0,
  transformOrigin: 'left center',
  background: BRAND.parchment,
});

const Box = styled('button')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  borderRadius: 999,
  WebkitTapHighlightColor: 'transparent',
});

/**
 * Both labels sit in the same place and cross-fade. `lineHeight: 1` centred in
 * the box leaves the two baselines about a fifth of a pixel apart, which is
 * what "sharing the unselected label's baseline" comes to in a browser.
 */
const Label = styled('span')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  fontFamily: FONTS.serif,
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});

const IconLayer = styled('span')({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

/**
 * Sign In: no box, and no highlight ever. Right-anchored, so it overhangs the
 * remainder on its left rather than being centred in it.
 */
const SignIn = styled('button')({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  lineHeight: 1,
  fontFamily: FONTS.serif,
  fontWeight: 400,
  color: BRAND.parchment,
  whiteSpace: 'nowrap',
  WebkitTapHighlightColor: 'transparent',
  '&:hover': { opacity: 0.75 },
  '&:active': { opacity: 0.75 },
});

interface Props {
  ref?: Ref<ViewToggleHandle>;
  /** Which item is showing. */
  value: ToggleValue;
  /** Which items the pill holds. */
  pill: TogglePill;
  /** A phone's pill sits on the page; every desktop width lets the page through. */
  fill?: keyof typeof TOGGLE_FILL;
  /** 1 everywhere but the narrowest desktop windows, which cannot fit one. */
  scale?: number;
  onSelect: (item: ToggleItem) => void;
  onSignIn: () => void;
}

export default function ViewToggle({
  ref,
  value,
  pill,
  fill = 'phone',
  scale = 1,
  onSelect,
  onSignIn,
}: Props) {
  const m = useMemo(() => toggleMetrics(pill, scale), [pill, scale]);

  const nodes = useRef<{
    hl: HTMLDivElement | null;
    capR: HTMLSpanElement | null;
    mid: HTMLSpanElement | null;
    on: (HTMLElement | null)[];
    off: (HTMLElement | null)[];
  }>({ hl: null, capR: null, mid: null, on: [], off: [] });

  /**
   * Sign In's inset from the pill's right edge. 25.6 is the measurement, and it
   * holds wherever the label fits in the 55px the boxes leave with its gap to
   * the last box. Our Canela Deck renders "Sign In" about 3px wider than the
   * reference draws it, which would put its S under the My Schedule highlight
   * and take the letter with it, so where it does not fit the label gives up
   * its inset instead and keeps the gap: an S touching the highlight reads as
   * part of it.
   */
  const signIn = useRef<HTMLButtonElement | null>(null);
  const [signInRight, setSignInRight] = useState(m.signInRight);
  useLayoutEffect(() => {
    const last = m.boxes[m.boxes.length - 1];
    const fit = () => {
      const el = signIn.current;
      if (!el) return;
      const room = m.width - (last.x + last.w) - el.getBoundingClientRect().width - m.signInGap;
      setSignInRight(Math.min(m.signInRight, room));
    };
    fit();
    // Again once Canela Deck is in: the fallback serif measures wider, and a
    // first paint against it would leave the label further in than it needs.
    let live = true;
    void document.fonts?.ready.then(() => live && fit());
    return () => {
      live = false;
    };
  }, [m]);

  /** The index last painted, so a deferred `value` change does not restart the move. */
  const painted = useRef<number | null>(null);
  const paintedFor = useRef<string>('');

  const paint = useCallback(
    (index: number, ms: number, ease: string) => {
      const n = nodes.current;
      const last = m.boxes.length - 1;
      const at = Math.max(0, Math.min(last, index));
      const i = Math.min(Math.floor(at), last);
      const f = at - i;
      const a = m.boxes[i];
      const b = m.boxes[Math.min(i + 1, last)];
      const x = a.x + (b.x - a.x) * f;
      const w = a.w + (b.w - a.w) * f;
      const move = ms ? `transform ${ms}ms ${ease}` : 'none';
      const fade = ms ? `opacity ${ms}ms ${ease}` : 'none';

      if (n.hl) {
        n.hl.style.transition = move;
        n.hl.style.transform = `translateX(${x}px)`;
      }
      if (n.capR) {
        n.capR.style.transition = move;
        n.capR.style.transform = `translateX(${w - m.highlight}px)`;
      }
      if (n.mid) {
        n.mid.style.transition = move;
        n.mid.style.transform = `scaleX(${Math.max(0, w - m.highlight) / m.highlight})`;
      }
      for (let k = 0; k <= last; k += 1) {
        const on = k === i ? 1 - f : k === i + 1 ? f : 0;
        const onEl = n.on[k];
        const offEl = n.off[k];
        if (onEl) {
          onEl.style.transition = fade;
          onEl.style.opacity = String(on);
        }
        if (offEl) {
          offEl.style.transition = fade;
          offEl.style.opacity = String(1 - on);
        }
      }
      painted.current = at;
    },
    [m],
  );

  useImperativeHandle(ref, () => ({ follow: paint }), [paint]);

  // A tap paints before `value` catches up, so only a change it has not already
  // played — a browser's back button, say — moves it here.
  useLayoutEffect(() => {
    const found = m.boxes.findIndex((b) => b.key === value);
    const target = found >= 0 ? found : Math.min(painted.current ?? 0, m.boxes.length - 1);
    const key = `${pill}:${scale}`;
    const jump = painted.current === null || paintedFor.current !== key;
    paintedFor.current = key;
    if (!jump && painted.current === target) return;
    paint(target, jump ? 0 : TOGGLE_MOVE_MS, TOGGLE_EASE);
  }, [value, pill, scale, m, paint]);

  /** Arrow keys walk the radios, as a radio group's do. */
  const onKeyDown = (index: number) => (e: React.KeyboardEvent) => {
    const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = (index + step + m.boxes.length) % m.boxes.length;
    onSelect(m.boxes[next].key);
  };

  return (
    <Pill
      role="radiogroup"
      aria-label="Switch view"
      style={{ width: m.width, height: m.height, background: TOGGLE_FILL[fill] }}
    >
      <Highlight
        ref={(el) => {
          nodes.current.hl = el;
        }}
        aria-hidden="true"
        style={{ top: m.inset, height: m.highlight }}
      >
        <Cap style={{ width: m.highlight, height: m.highlight }} />
        <Cap
          ref={(el) => {
            nodes.current.capR = el;
          }}
          style={{ width: m.highlight, height: m.highlight }}
        />
        <Mid
          ref={(el) => {
            nodes.current.mid = el;
          }}
          style={{ left: m.highlight / 2, width: m.highlight, height: m.highlight }}
        />
      </Highlight>

      {m.boxes.map((box, i) => (
        <Box
          key={box.key}
          type="button"
          role="radio"
          aria-checked={value === box.key}
          aria-label={box.key === 'account' ? 'Account' : undefined}
          tabIndex={value === box.key ? 0 : -1}
          onKeyDown={onKeyDown(i)}
          onClick={() => onSelect(box.key)}
          style={{ left: box.x, width: box.w }}
        >
          {box.key === 'account' ? (
            <>
              <IconLayer
                ref={(el) => {
                  nodes.current.off[i] = el;
                }}
              >
                <AccountIcon size={m.icon} />
              </IconLayer>
              <IconLayer
                ref={(el) => {
                  nodes.current.on[i] = el;
                }}
              >
                <AccountIcon size={m.icon} selected />
              </IconLayer>
            </>
          ) : (
            <>
              <Label
                ref={(el) => {
                  nodes.current.off[i] = el;
                }}
                style={{ fontSize: m.labelOff, fontWeight: 400, color: BRAND.parchment }}
              >
                {TOGGLE_LABELS[box.key]}
              </Label>
              <Label
                ref={(el) => {
                  nodes.current.on[i] = el;
                }}
                style={{ fontSize: m.labelOn, fontWeight: 700, color: '#000' }}
              >
                {TOGGLE_LABELS[box.key]}
              </Label>
            </>
          )}
        </Box>
      ))}

      {m.signIn && (
        <SignIn
          ref={signIn}
          type="button"
          onClick={onSignIn}
          style={{ right: signInRight, fontSize: m.labelOff }}
        >
          Sign In
        </SignIn>
      )}
    </Pill>
  );
}
